import { Phone, MessageCircle, Mail, MapPin, Clock } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { MapSection } from "@/components/home/MapSection";
import { BeforeAfterGallery } from "@/components/common/BeforeAfterGallery";
import { GoogleReviewsWidget } from "@/components/common/GoogleReviewsWidget";
import { Seo } from "@/components/common/Seo";
import { buildBreadcrumbSchema } from "@/lib/schema";
import { CONTACT } from "@/data/site";

const cards = [
  { icon: Phone, title: "Call Now", value: CONTACT.phoneDisplay, href: CONTACT.phoneTel, testid: "contact-call", cta: "Tap to call" },
  { icon: MessageCircle, title: "WhatsApp", value: "Chat with our team", href: CONTACT.whatsapp, testid: "contact-whatsapp", cta: "Open WhatsApp", external: true },
  { icon: Mail, title: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email", cta: "Send email" },
];

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Dr. Ankesh Seth | Book a Dentist Appointment in Varanasi"
        description="Book your dental appointment with Dr. Ankesh Seth in Paharia, Varanasi. Call, WhatsApp or email — clinic address, timings and directions inside."
        path="/contact"
        jsonLd={buildBreadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])}
      />
      <PageHero
        eyebrow="Contact"
        titleLines={["Book Your", "Appointment"]}
        subtitle="We'd love to help you smile with confidence. Call, WhatsApp or email us — our friendly team in Paharia will find a time that suits you."
        crumbs={[{ label: "Contact" }]}
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-28 lg:px-20">
          <div className="grid gap-4 md:grid-cols-3">
            {cards.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <a
                  href={c.href}
                  data-testid={c.testid}
                  {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="group flex h-full flex-col rounded-2xl border border-brand-line bg-brand-soft p-8 transition-all duration-300 hover:-translate-y-1 hover:border-brand-blue/40"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue text-white">
                    <c.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-2xl font-medium text-brand-dark">{c.title}</h3>
                  <p className="mt-2 flex-1 break-words text-brand-muted">{c.value}</p>
                  <span className="mt-4 text-sm font-semibold text-brand-blue">{c.cta} →</span>
                </a>
              </Reveal>
            ))}
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Reveal>
              <div className="flex h-full items-start gap-4 rounded-2xl border border-brand-line p-8">
                <MapPin className="mt-1 h-6 w-6 shrink-0 text-brand-green" />
                <div>
                  <h3 className="font-display text-xl font-medium text-brand-dark">Clinic Address</h3>
                  <p className="mt-2 text-brand-muted">{CONTACT.address}</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="flex h-full items-start gap-4 rounded-2xl border border-brand-line p-8">
                <Clock className="mt-1 h-6 w-6 shrink-0 text-brand-green" />
                <div>
                  <h3 className="font-display text-xl font-medium text-brand-dark">Clinic Timings</h3>
                  <div className="mt-2 text-brand-muted">
                    {CONTACT.timings.map((t) => (
                      <p key={t.day}>{t.day}: {t.hours}</p>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <GoogleReviewsWidget />
      <BeforeAfterGallery muted />
      <MapSection />
    </div>
  );
}
